import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/shadcn/button';
import { cn } from '@/lib/utils';
import { Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The shared look of the action row on the detail pages (lead, contact,
 * company, project). Leads set the pattern: an outline button, small, icon plus
 * label, never wrapping mid-label.
 *
 * The row itself ends with Edit → Transfer → Delete, in that order, so the
 * destructive action is always the last thing on the right.
 */
export const DETAIL_ACTION_BUTTON_CLASS = 'gap-2 whitespace-nowrap';

/**
 * Groups buttons into a single segmented control — shared borders, no gap — for
 * actions that belong together and should never be separated or reordered
 * (add meeting / add call).
 */
export function ConnectedActions({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div
            className={cn(
                'inline-flex shrink-0 items-center',
                '[&>button]:rounded-none [&>button:first-child]:rounded-l-md [&>button:last-child]:rounded-r-md',
                '[&>button+button]:-ml-px',
                className,
            )}
        >
            {children}
        </div>
    );
}

/**
 * Delete is icon-only everywhere: the trash glyph in the destructive colour
 * carries the meaning, and the label lives on the confirmation dialog.
 */
export function DeleteActionButton({ label, onClick, className }: { label: string; onClick: () => void; className?: string }) {
    return (
        <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className={cn('text-destructive hover:text-destructive', className)}
            onClick={onClick}
            aria-label={label}
            title={label}
        >
            <Trash2 className="h-4 w-4" />
        </Button>
    );
}

/**
 * The confirmation every delete action opens. Built on the shadcn AlertDialog
 * so the destructive choice is modal and cannot be dismissed by a stray click
 * on the overlay.
 */
export function DeleteConfirmAlert({
    open,
    onOpenChange,
    title,
    description,
    cancelLabel,
    confirmLabel,
    onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    cancelLabel: string;
    confirmLabel: string;
    onConfirm: () => void;
}) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription>{description}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
                    <AlertDialogAction className={cn(buttonVariants({ variant: 'destructive' }))} onClick={onConfirm}>
                        {confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
